import React, { useState } from "react"
import { PlusCircleIcon, XMarkIcon } from "@heroicons/react/24/outline"

import Modal from "../../../components/modal"
import Input from "../../../components/input"
import { useNana } from "../../../hooks/useNana"
import * as Commands from "../../../../model/commands"
import { Idea, Project } from "../../../../model/types"
import IdeaBox from "../../../components/idea_box"
import { set } from "lodash"
import { AutocompleteResult } from "../../../components/autocomplete"
import GoalAutocomplete from "../../../components/goal_autocomplete"
import { generatePublicId } from "../../../../model/public_id"

export default function NewProjectButton() {
  const [isOpen, setIsOpen] = useState(false)
  const [title, setTitle] = useState("")
  const [selectedGoal, setSelectedGoal] = useState<Idea | null>(null)

  const { model, commander } = useNana()

  const open = () => {
    setIsOpen(true)
    setTitle("")
    setSelectedGoal(null)
  }

  const submit = () => {
    const project: Project = {
      id: generatePublicId(),
      name: title,
      status: "in_progress",
      createdAt: new Date(),
      updatedAt: new Date(),
      goalId: selectedGoal?.id,
      ideaIds: [],
    }
    commander.exec(
      new Commands.CreateProjectCommand({ id: project.id, project })
    )
    setIsOpen(false)
  }

  return (
    <div data-testid="new-project-button-and-form">
      <header className="">
        <button
          type="button"
          onClick={() => open()}
          className="flex flex-col items-center gap-x-1.5 rounded-md bg-blue-50 dark:bg-gray-800 px-2.5 py-1.5 text-sm font-semibold text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
          data-testid="new-project-button"
        >
          <PlusCircleIcon className="h-6 w-6" />
          New project
        </button>
      </header>
      <Modal
        open={isOpen}
        primaryButton={{
          text: "Add",
          action: () => submit(),
          disabled: false,
        }}
        secondaryButton={{
          text: "Cancel",
          action: () => setIsOpen(false),
          disabled: false,
        }}
        leftButton={{
          text: "",
          action: null,
          disabled: true,
        }}
        close={() => setIsOpen(false)}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
        >
          <Input
            label="Title"
            name="title"
            type="text"
            placeholder="Title of project"
            error={null}
            disabled={false}
            value={title}
            onChange={(n) => setTitle(n)}
            testid="new-project-title-input"
          />
          <label className="block mt-4 mb-2 text-sm font-medium leading-6 text-gray-900 dark:text-gray-50">
            Associated goal (optional)
          </label>
          {selectedGoal ? (
            <div className="flex justify-between items-center text-sm text-gray-700 dark:text-gray-100">
              <div className="flex justify-start items-center gap-3">
                <IdeaBox idea={selectedGoal} />
                <div>{selectedGoal.title}</div>
              </div>
              <div
                onClick={() => setSelectedGoal(null)}
                className="cursor-pointer"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500 hover:text-gray-300" />
              </div>
            </div>
          ) : (
            <GoalAutocomplete
              select={(result: AutocompleteResult<Idea>, _filter) => {
                if (result.type == "none") return true
                const goal = result.item
                setSelectedGoal(goal)
                return true
              }}
              onEscape={() => setIsOpen(false)}
              inputStyle="form"
            />
          )}
        </form>
      </Modal>
    </div>
  )
}
